import React, { useMemo } from "react";
import { DRAWING_TOOLS, DRAWING_TOOL_STATUS } from "./drawingModel";
import "./DrawToolsPanel.css";

function DrawToolsPanel({
  selectedToolId = null,
  drawings = [],
  onSelectTool,
  onSelectDrawing,
  onToggleDrawingLock,
  onToggleDrawingVisibility,
  onDeleteDrawing,
  onClearDrawings,
}) {
  const selectedTool = useMemo(
    () => DRAWING_TOOLS.find((tool) => tool.id === selectedToolId) || null,
    [selectedToolId]
  );

  return (
    <div className="drawToolsPanel">
      <div className="drawToolsIntro">
        <strong>Draw Tools</strong>
        <p>Drawings are chart annotations only. They never place or modify brokerage orders.</p>
      </div>

      <div className="drawToolsGrid" aria-label="Drawing tools">
        {DRAWING_TOOLS.map((tool) => {
          const available = tool.status === DRAWING_TOOL_STATUS.AVAILABLE;
          const active = selectedToolId === tool.id;
          return (
            <button
              key={tool.id}
              type="button"
              className={`drawToolButton${active ? " active" : ""}`}
              disabled={!available}
              aria-pressed={active}
              onClick={() => onSelectTool?.(tool.id)}
            >
              <span>{tool.name}</span>
              <small>
                {available
                  ? `${tool.pointsRequired} point${tool.pointsRequired === 1 ? "" : "s"}`
                  : "Planned - fail closed"}
              </small>
            </button>
          );
        })}
      </div>

      <div className="drawToolSelection" role="status">
        <span>Selected tool</span>
        <strong>{selectedTool ? selectedTool.name : "None"}</strong>
      </div>

      <div className="drawingsManager">
        <div className="drawingsManagerHeader">
          <div>
            <span>Chart drawings</span>
            <strong>{drawings.length}</strong>
          </div>
          <button type="button" disabled={drawings.length === 0} onClick={() => onClearDrawings?.()}>
            Clear all
          </button>
        </div>

        {drawings.length === 0 ? (
          <div className="drawingsEmpty">
            <strong>No drawings yet</strong>
            <p>Select an available tool, then place its anchor points on the chart.</p>
          </div>
        ) : (
          <div className="drawingList">
            {drawings.map((drawing) => {
              const tool = DRAWING_TOOLS.find((item) => item.id === drawing.toolId);
              const name = tool?.name || drawing.toolId;
              return (
                <article
                  key={drawing.id}
                  className={`drawingListItem${drawing.isSelected ? " selected" : ""}`}
                >
                  <button
                    type="button"
                    className="drawingSelectButton"
                    aria-pressed={Boolean(drawing.isSelected)}
                    onClick={() => onSelectDrawing?.(drawing.id)}
                  >
                    <strong>{name}</strong>
                    <span>{drawing.isVisible ? "Visible" : "Hidden"}</span>
                  </button>
                  <div className="drawingItemActions">
                    <button
                      type="button"
                      aria-label={`${drawing.isLocked ? "Unlock" : "Lock"} ${name}`}
                      aria-pressed={Boolean(drawing.isLocked)}
                      onClick={() => onToggleDrawingLock?.(drawing.id)}
                    >
                      {drawing.isLocked ? "Unlock" : "Lock"}
                    </button>
                    <button
                      type="button"
                      aria-label={`${drawing.isVisible ? "Hide" : "Show"} ${name}`}
                      onClick={() => onToggleDrawingVisibility?.(drawing.id)}
                    >
                      {drawing.isVisible ? "Hide" : "Show"}
                    </button>
                    <button
                      type="button"
                      aria-label={`Delete ${name}`}
                      onClick={() => onDeleteDrawing?.(drawing.id)}
                    >
                      Delete
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default DrawToolsPanel;
